/**
 * ============================================================================
 * WITHDRAWAL FORM COMPONENT - 100% Ready (Cameroon Focus)
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useWallet } from '../../hooks/useWallet';
import { useWalletStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import { validateCameroonPhone, formatPhoneInput, getFullPhoneNumber } from '../../utils/phoneValidation';

/**
 * Withdrawal Form Component
 * @param {Object} props
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onCancel - Cancel callback
 * @returns {JSX.Element}
 */
const WithdrawalForm = ({ onSuccess, onCancel }) => {
  const { requestWithdrawal, estimateWithdrawalFees, loading } = useWallet();
  const { balance, wallet } = useWalletStore();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, language } = useI18n();

  const [formData, setFormData] = useState({
    amount: '',
    method: 'mtn_mobile_money',
    phone: '',
    accountNumber: '',
    bankName: '',
  });

  const [errors, setErrors] = useState({});
  const [feeEstimate, setFeeEstimate] = useState(null);

  const availableBalance = balance?.available || 0;
  const minWithdrawal = 1000; // XAF minimum

  // Cameroon-focused withdrawal methods
  const withdrawalMethods = [
    {
      id: 'mtn_mobile_money',
      label: language === 'fr' ? 'MTN Mobile Money' : 'MTN Mobile Money',
      icon: '📱',
      color: 'from-yellow-500 to-orange-500',
      placeholder: language === 'fr' ? 'Numéro de téléphone' : 'Phone number',
      requiresPhone: true,
    },
    {
      id: 'orange_money',
      label: language === 'fr' ? 'Orange Money' : 'Orange Money',
      icon: '🟠',
      color: 'from-orange-500 to-red-500',
      placeholder: language === 'fr' ? 'Numéro de téléphone' : 'Phone number',
      requiresPhone: true,
    },
    {
      id: 'bank_transfer',
      label: language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
      icon: '🏦',
      color: 'from-blue-500 to-cyan-500',
      placeholder: language === 'fr' ? 'Numéro de compte' : 'Account number',
      requiresPhone: false,
    },
  ];

  const currentMethod = withdrawalMethods.find((m) => m.id === formData.method) || withdrawalMethods[0];

  // Estimate fees when amount changes (only call API when amount >= min)
  useEffect(() => {
    const amount = parseFloat(formData.amount);
    if (amount >= minWithdrawal && formData.method) {
      estimateWithdrawalFees({
        amount,
        withdrawalMethod: formData.method,
      })
        .then((response) => {
          const data = response?.data ?? response;
          const fee =
            data?.breakdown?.actualFee ??
            data?.withdrawalFee ??
            Math.max(amount * 0.01, 100);
          setFeeEstimate({ fee, total: amount });
        })
        .catch(() => {
          const fee = Math.max(amount * 0.01, 100);
          setFeeEstimate({ fee, total: amount });
        });
    } else if (amount > 0 && amount < minWithdrawal) {
      const fee = Math.max(amount * 0.01, 100);
      setFeeEstimate({ fee, total: amount });
    } else {
      setFeeEstimate(null);
    }
  }, [formData.amount, formData.method, minWithdrawal, estimateWithdrawalFees]);

  const handleChange = (field, value) => {
    if (field === 'phone') {
      value = formatPhoneInput(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount =
        language === 'fr'
          ? 'Veuillez entrer un montant valide'
          : 'Please enter a valid amount';
    } else if (amount < minWithdrawal) {
      newErrors.amount =
        language === 'fr'
          ? `Le retrait minimum est ${formatCurrency(minWithdrawal, wallet?.currency || 'XAF')}`
          : `Minimum withdrawal is ${formatCurrency(minWithdrawal, wallet?.currency || 'XAF')}`;
    } else if (amount > availableBalance) {
      newErrors.amount =
        language === 'fr' ? 'Solde insuffisant' : 'Insufficient balance';
    }

    if (currentMethod.requiresPhone) {
      if (!formData.phone.trim()) {
        newErrors.phone =
          language === 'fr'
            ? 'Le numéro de téléphone est requis'
            : 'Phone number is required';
      } else {
        const validation = validateCameroonPhone(formData.phone);
        if (!validation.valid) {
          newErrors.phone = validation.error;
        }
      }
    } else {
      if (!formData.accountNumber.trim()) {
        newErrors.accountNumber =
          language === 'fr'
            ? 'Le numéro de compte est requis'
            : 'Account number is required';
      }
      if (!formData.bankName.trim()) {
        newErrors.bankName =
          language === 'fr' ? 'Le nom de la banque est requis' : 'Bank name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const withdrawalData = {
        amount: parseFloat(formData.amount),
        withdrawalMethod: formData.method,
      };

      if (currentMethod.requiresPhone) {
        withdrawalData.destinationPhone = getFullPhoneNumber(formData.phone);
      } else {
        withdrawalData.destinationAccount = formData.accountNumber.trim();
        withdrawalData.destinationBank = formData.bankName.trim();
      }

      await requestWithdrawal(withdrawalData);
      showSuccess(
        language === 'fr'
          ? 'Demande de retrait soumise!'
          : 'Withdrawal request submitted!'
      );
      if (onSuccess) onSuccess();
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr'
            ? 'Échec de la demande de retrait'
            : 'Failed to request withdrawal')
      );
    }
  };

  const amount = parseFloat(formData.amount) || 0;
  const fee = feeEstimate?.fee || Math.max(amount * 0.01, 100);
  const currency = wallet?.currency || 'XAF';

  return (
    <form onSubmit={handleSubmit} className="wallet-wt-form space-y-6">
      {/* Available Balance */}
      <div className="dashboard-wt-card p-4 rounded-xl border border-[rgba(34,197,94,0.2)] bg-[rgba(34,197,94,0.06)]">
        <p className="dashboard-wt-card-desc text-sm mb-1">
          {language === 'fr' ? 'Solde disponible' : 'Available Balance'}
        </p>
        <p className="text-2xl font-bold text-[#22c55e]">
          {formatCurrency(availableBalance, currency)}
        </p>
      </div>

      {/* Withdrawal Method */}
      <div>
        <label className="block text-sm font-semibold text-[#fafafa] mb-2">
          {language === 'fr' ? 'Méthode de retrait' : 'Withdrawal Method'}
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {withdrawalMethods.map((method) => (
            <button
              key={method.id}
              type="button"
              onClick={() => handleChange('method', method.id)}
              className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${
                formData.method === method.id
                  ? 'border-[#22c55e] bg-[rgba(34,197,94,0.1)] ring-1 ring-[#22c55e]'
                  : 'border-[rgba(255,255,255,0.08)] bg-[#1a1a1a] hover:border-[rgba(34,197,94,0.4)]'
              }`}
            >
              <span className="text-2xl">{method.icon}</span>
              <span className="font-medium text-[#fafafa]">{method.label}</span>
              {formData.method === method.id && (
                <span className="ml-auto w-6 h-6 rounded-full bg-[#22c55e] flex items-center justify-center text-white text-xs">✓</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Amount */}
      <div>
        <label className="block text-sm font-semibold text-[#fafafa] mb-2">
          {language === 'fr' ? 'Montant (FCFA)' : 'Amount (FCFA)'}
        </label>
        <input
          type="number"
          value={formData.amount}
          onChange={(e) => handleChange('amount', e.target.value)}
          placeholder={language === 'fr' ? 'Ex: 50000' : 'e.g. 50000'}
          min={minWithdrawal}
          max={availableBalance > minWithdrawal ? availableBalance : undefined}
          step="1"
          className="dashboard-wt-input"
        />
        {errors.amount && (
          <p className="mt-1 text-sm text-red-400">{errors.amount}</p>
        )}
        <p className="mt-1 text-xs text-[#a3a3a3]">
          {language === 'fr' ? `Minimum : ${minWithdrawal.toLocaleString('fr-FR')} FCFA` : `Minimum: ${minWithdrawal.toLocaleString()} FCFA`}
        </p>
      </div>

      {/* Phone or Bank details */}
      {currentMethod.requiresPhone ? (
        <div>
          <label className="block text-sm font-semibold text-[#fafafa] mb-2">
            {language === 'fr' ? 'Numéro de téléphone' : 'Phone Number'}
          </label>
          <div className="flex gap-2">
            <span className="px-4 py-3 rounded-lg bg-[#1a1a1a] border border-[rgba(255,255,255,0.08)] text-[#a3a3a3] font-medium">
              +237
            </span>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="6XX XXX XXX"
              className="flex-1 dashboard-wt-input"
            />
          </div>
          {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone}</p>}
          <p className="mt-1 text-xs text-[#a3a3a3]">
            {language === 'fr' ? '9 chiffres (6, 7, 8 ou 9)' : '9 digits starting with 6, 7, 8 or 9'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-[#fafafa]">
              {language === 'fr' ? 'Nom de la banque' : 'Bank Name'}
            </label>
            <input
              type="text"
              value={formData.bankName}
              onChange={(e) => handleChange('bankName', e.target.value)}
              placeholder="e.g. Afriland First Bank"
              className="dashboard-wt-input"
            />
            {errors.bankName && <p className="text-sm text-red-400">{errors.bankName}</p>}
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-[#fafafa]">
              {language === 'fr' ? 'Numéro de compte' : 'Account Number'}
            </label>
            <input
              type="text"
              value={formData.accountNumber}
              onChange={(e) => handleChange('accountNumber', e.target.value)}
              placeholder={language === 'fr' ? 'Numéro de compte' : 'Account number'}
              className="dashboard-wt-input"
            />
            {errors.accountNumber && <p className="text-sm text-red-400">{errors.accountNumber}</p>}
          </div>
        </>
      )}

      {/* Fee breakdown */}
      {amount > 0 && (
        <div className="fee-box space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-[#a3a3a3]">{language === 'fr' ? 'Montant' : 'Amount'}</span>
            <span className="text-[#fafafa] font-medium">{formatCurrency(amount, currency)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#a3a3a3]">{language === 'fr' ? 'Frais' : 'Fee'}</span>
            <span className="text-[#fafafa] font-medium">{formatCurrency(fee, currency)}</span>
          </div>
          <div className="border-t border-[rgba(255,255,255,0.08)] pt-3 mt-3 flex justify-between items-center">
            <span className="font-semibold text-[#fafafa]">
              {language === 'fr' ? 'Vous recevrez' : "You'll receive"}
            </span>
            <span className="text-xl font-bold text-[#22c55e]">
              {formatCurrency(Math.max(0, amount - fee), currency)}
            </span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="dashboard-wt-btn-secondary flex-1"
        >
          {language === 'fr' ? 'Annuler' : 'Cancel'}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="dashboard-wt-btn-primary flex-1"
        >
          {loading ? (language === 'fr' ? 'Envoi...' : 'Submitting...') : (language === 'fr' ? 'Demander le retrait' : 'Request Withdrawal')} →
        </button>
      </div>
    </form>
  );
};

export default WithdrawalForm;
